import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { Observable, Subscription, throwError } from 'rxjs';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../../forms/fields/checkbox/ziro-checkbox.component';
import { ZiroRadioConfig } from '../../../../forms/fields/radio/ziro-radio.component';
import { HtmlInputType, InputSize, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { DidMatchType, ZpmProxyFeatureSettingsEntity } from '../proxy-feature-settings.component';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../button/button.component';
import { catchError, tap } from 'rxjs/operators';
import { ZpmProxySettings } from '../../../../shared/models/generated/ziroModels';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { MicrosoftProxyFeatureSettingsResource } from '../../../resources/microsoft-proxy-feature-settings.resource';
import { ToastService } from '../../../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ziro-ms-proxy-feature-settings-form',
  templateUrl: './ms-proxy-feature-settings-form.component.html',
  standalone: false,
})
export class MsProxyFeatureSettingsFormComponent
  extends ZiroFormAbstractDirective<ZpmProxyFeatureSettingsEntity>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _bottomNavService = inject(BottomNavService);
  private _microsoftProxyFeatureSettingsResource = inject(MicrosoftProxyFeatureSettingsResource);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);

  dialPlanSyncToggleEnabled: boolean;
  lastDigitsMatchEnabled: boolean;
  lastDigitsMatchCount: number;

  private _subs = new Subscription();
  protected readonly ziroIcons = ZiroIcons;

  formConfig: ZiroFormConfig = {
    fields: {
      onPremAdWriteDownEnabled: {
        dataAutomation: 'proxy-settings-ad-write-down',
        required: true,
        componentConfig: new ZiroCheckboxConfig({
          size: HtmlSwitchSize.LG,
          checkboxType: HtmlCheckboxType.SWITCH,
        }),
      },
      cucmPhoneNumberAssignmentCheckEnabled: {
        dataAutomation: 'proxy-settings-dial-plan-sync',
        required: true,
        componentConfig: new ZiroCheckboxConfig({
          size: HtmlSwitchSize.LG,
          checkboxType: HtmlCheckboxType.SWITCH,
        }),
      },
      cucmPhoneNumberAssignmentMatchType: {
        dataAutomation: 'did-match-type',
        required: true,
        componentConfig: new ZiroRadioConfig({
          isDisplayVertical: true,
          alignLeftNoLabel: true,
          buttons: [
            {
              value: DidMatchType.EXACT_MATCH,
              label:
                'tkey;admin.proxy_server_management.proxy_feature_settings.number_assignment_check.option.exact_match.zpm',
            },
            {
              value: DidMatchType.END_MATCH,
              label: this._getTranslatedEndMatchLabel(),
            },
          ],
          inline: true,
        }),
      },
      cucmPhoneNumberAssignmentLastDigitsMatch: {
        label:
          'tkey;admin.proxy_server_management.proxy_feature_settings.number_assignment_check.option.end_match.count',
        dataAutomation: 'last-matching-digits-count',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.NUMBER,
          inputSize: InputSize.SM,
        }),
        validation: [
          {
            validator: (value: string, cucmPhoneNumberAssignmentMatchType: DidMatchType) => {
              if (cucmPhoneNumberAssignmentMatchType === DidMatchType.EXACT_MATCH) {
                return ZiroFormsValidationState.VALID;
              }
              return Number(value) > 0 && Number(value) < 11
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: this._translateService.instant(
              'tkey;admin.proxy_server_management.proxy_feature_settings.number_assignment_check.option.end_match.count.invalid_message',
            ),
            injectValuesFromFields: ['cucmPhoneNumberAssignmentMatchType'],
          },
        ],
      },
    },
  };

  ngOnInit(): void {
    const ziroFormUpdateSub = this.ziroFormsUpdate$.subscribe(
      (update: ZiroFormsUpdate<ZpmProxyFeatureSettingsEntity>) => {
        this.dialPlanSyncToggleEnabled = update.new.cucmPhoneNumberAssignmentCheckEnabled;
        this.lastDigitsMatchEnabled = update.new.cucmPhoneNumberAssignmentMatchType === DidMatchType.END_MATCH;
        this.lastDigitsMatchCount =
          !!update.new.cucmPhoneNumberAssignmentLastDigitsMatch && this.lastDigitsMatchEnabled
            ? update.new.cucmPhoneNumberAssignmentLastDigitsMatch
            : 0;

        const cucmPhoneNumberAssignmentMatchType = this.fieldComponents.find(
          (field) => field.fieldId() === 'cucmPhoneNumberAssignmentMatchType',
        );
        cucmPhoneNumberAssignmentMatchType.applyComponentConfig(
          new ZiroRadioConfig({
            ...this.formConfig.fields['cucmPhoneNumberAssignmentMatchType'].componentConfig,
            buttons: [
              {
                value: DidMatchType.EXACT_MATCH,
                label:
                  'tkey;admin.proxy_server_management.proxy_feature_settings.number_assignment_check.option.exact_match.zpm',
              },
              {
                value: DidMatchType.END_MATCH,
                label: this._getTranslatedEndMatchLabel(),
              },
            ],
          }),
        );
        const cucmPhoneNumberAssignmentLastDigitsMatch = this.fieldComponents.find(
          (field) => field.fieldId() === 'cucmPhoneNumberAssignmentLastDigitsMatch',
        );
        cucmPhoneNumberAssignmentLastDigitsMatch.applyState({
          ...cucmPhoneNumberAssignmentLastDigitsMatch.state,
          required: !!this.lastDigitsMatchEnabled,
        });
      },
    );
    this._subs.add(ziroFormUpdateSub);
    this._initBottomNav();
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private _initBottomNav: () => void = () => {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'proxy-feature-settings-save',
          dataAutomation: 'proxy-feature-settings-save-button',
          label: 'tkey;global.button.save.text',
          icon: ZiroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          cb: () => {
            this._validateAndSubmitSource.next(true);
          },
        },
      ]),
    );
  };

  protected submit(): Observable<ZpmProxySettings> {
    this._setPending(true);
    const apiData = this._toApiData();
    const apiRequest = this._microsoftProxyFeatureSettingsResource.put(apiData);

    return apiRequest.pipe(
      tap(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.SERVER,
          'tkey;shared.toast.save.success.title',
          'tkey;proxy_feature_settings.toast.success',
        );
        this._setPending(false);
      }),
      catchError((error) => {
        this.isSubmitting = false;
        this._validateAndSubmitSource.next(false);
        this._setPending(false);
        return throwError(() => error);
      }),
    );
  }

  private _toApiData(): ZpmProxySettings {
    return {
      onPremAdWriteDownEnabled: this.entity.onPremAdWriteDownEnabled,
      cucmPhoneNumberAssignmentCheckEnabled: this.entity.cucmPhoneNumberAssignmentCheckEnabled,
      cucmPhoneNumberAssignmentLastDigitsMatch:
        this.entity.cucmPhoneNumberAssignmentMatchType === DidMatchType.EXACT_MATCH ||
        !this.entity.cucmPhoneNumberAssignmentCheckEnabled
          ? 0
          : this.entity.cucmPhoneNumberAssignmentLastDigitsMatch,
    };
  }

  private _setPending(setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'proxy-feature-settings-save',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  // this is done to keep the label alignment of the radio options on the left
  private _getTranslatedEndMatchLabel(): string {
    return this._translateService.instant(
      'tkey;admin.proxy_server_management.proxy_feature_settings.number_assignment_check.option.end_match.zpm',
      {
        lastDigitCount: this.lastDigitsMatchCount == 0 ? 'X' : this.lastDigitsMatchCount,
      },
    );
  }
}
